/* eslint-disable */
/**
 * liveness-core.js — pure geometry + challenge evaluation, shared by the browser (kiosk) and the
 * Node server. No DOM, no TensorFlow, no I/O: everything here is a function of numbers, which is
 * what makes it testable and lets the server re-check exactly what the kiosk claims it saw.
 *
 * Landmarks are the 68-point iBUG layout used by face-api.js (array of {x, y} in RAW camera pixels,
 * i.e. before any mirroring for display).
 *
 * Sign convention: yaw > 0  => the nose is displaced toward the right of the raw image, which is the
 * subject's LEFT. So "turn_left" means "turn your head to YOUR left" (the left of the mirrored preview).
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.LivenessCore = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  const STEP_TYPES = ['blink', 'turn_left', 'turn_right', 'smile'];

  const DEFAULTS = {
    turnThreshold: 0.35,      // |yaw| a deliberate head turn must reach (~18 degrees)
    centerThreshold: 0.18,    // |yaw| at or below this counts as "facing the camera"
    blinkClosedRatio: 0.72,   // eye is "closed" when EAR <= baseline * this
    blinkOpenRatio: 0.85,     // ...and "open" again at baseline * this
    minBaselineEar: 0.15,     // below this the eyes are effectively always shut / landmarks are junk
    maxBlinkFrames: 12,       // a real blink is short; a long closure is not counted
    smileOn: 0.75,
    smileOff: 0.45,
    minFrames: 8,
    maxFrames: 900,
    minDurationMs: 1000,
    maxDurationMs: 60000,
    maxGapMs: 1500,           // a hole this big in the frame stream means the feed was spliced or stalled
    maxCenterJump: 0.9,       // face-centre move between frames, in face widths
    maxSizeRatio: 1.7,        // face width may not change by more than this factor between frames
    frontalYaw: 0.25,         // probes used for matching must be near-frontal
  };

  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

  function percentile(values, q) {
    const s = values.filter(isNum).sort((a, b) => a - b);
    if (!s.length) return NaN;
    const i = (s.length - 1) * q;
    const lo = Math.floor(i), hi = Math.ceil(i);
    return s[lo] + (s[hi] - s[lo]) * (i - lo);
  }
  const median = (values) => percentile(values, 0.5);

  // ---- geometry from 68 landmarks ---------------------------------------------------------------

  function eyeAspectRatio(pts, i0) {
    const v1 = dist(pts[i0 + 1], pts[i0 + 5]);
    const v2 = dist(pts[i0 + 2], pts[i0 + 4]);
    const h = dist(pts[i0], pts[i0 + 3]);
    return h > 0 ? (v1 + v2) / (2 * h) : 0;
  }
  /** Mean eye aspect ratio of both eyes (open ~0.25-0.35, closed < 0.18). */
  function earFromLandmarks(pts) {
    return (eyeAspectRatio(pts, 36) + eyeAspectRatio(pts, 42)) / 2;
  }
  /** Horizontal head rotation proxy in about [-1, 1]; 0 = frontal. See sign convention above. */
  function yawFromLandmarks(pts) {
    const half = (pts[16].x - pts[0].x) / 2;
    if (half <= 0) return 0;
    const mid = (pts[0].x + pts[16].x) / 2;
    return clamp((pts[30].x - mid) / half, -1.5, 1.5);
  }
  /** Vertical rotation proxy: nose position between eye line and chin (about 0.45-0.6 when level). */
  function pitchFromLandmarks(pts) {
    let ey = 0;
    for (let i = 36; i < 48; i++) ey += pts[i].y;
    ey /= 12;
    const span = pts[8].y - ey;
    return span > 0 ? (pts[30].y - ey) / span : 0;
  }
  /** In-plane tilt of the head in degrees (line through the eye centres). */
  function rollFromLandmarks(pts) {
    const c = (i0) => {
      let x = 0, y = 0;
      for (let i = i0; i < i0 + 6; i++) { x += pts[i].x; y += pts[i].y; }
      return { x: x / 6, y: y / 6 };
    };
    const a = c(36), b = c(42);
    return (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
  }
  function boxOfLandmarks(pts) {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const p of pts) { x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y); x1 = Math.max(x1, p.x); y1 = Math.max(y1, p.y); }
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  }

  // ---- step detectors ---------------------------------------------------------------------------

  function earBaseline(series) {
    return percentile(series.map((f) => f.ear), 0.8);
  }

  /** Returns the index just after the first complete blink at/after `from`, or -1. */
  function findBlink(series, from, baseline, o) {
    if (!isNum(baseline) || baseline < o.minBaselineEar) return -1;
    const closed = baseline * o.blinkClosedRatio;
    const open = baseline * o.blinkOpenRatio;
    const n = series.length;
    let i = from;
    while (i < n) {
      if (series[i].ear <= closed) {
        let j = i;
        while (j < n && series[j].ear <= closed) j++;
        const len = j - i;
        let openBefore = false, openAfter = false;
        for (let k = from; k < i; k++) if (series[k].ear >= open) { openBefore = true; break; }
        for (let k = j; k < Math.min(n, j + 6); k++) if (series[k].ear >= open) { openAfter = true; break; }
        if (len <= o.maxBlinkFrames && openBefore && openAfter) return j;
        i = j;
      } else i++;
    }
    return -1;
  }

  /** dir = +1 for turn_left (subject's left), -1 for turn_right. Must start from a centred / opposite pose. */
  function findTurn(series, from, dir, o) {
    let sawCenter = false;
    for (let i = from; i < series.length; i++) {
      const y = series[i].yaw * dir;
      if (!isNum(y)) continue;
      if (y <= o.centerThreshold) sawCenter = true;
      if (sawCenter && y >= o.turnThreshold) return i + 1;
    }
    return -1;
  }

  function findSmile(series, from, o) {
    let neutral = false;
    for (let i = from; i < series.length; i++) {
      const s = series[i].sm;
      if (!isNum(s)) continue;
      if (s <= o.smileOff) neutral = true;
      if (neutral && s >= o.smileOn) return i + 1;
    }
    return -1;
  }

  // ---- series sanity ----------------------------------------------------------------------------

  /**
   * series: [{ t (ms since challenge start), ear, yaw, sm (smile 0..1 or null), cx, cy, w, h, n (faces in view) }]
   */
  function checkSeries(series, o) {
    if (!Array.isArray(series)) return { ok: false, reason: 'no frame series' };
    if (series.length < o.minFrames) return { ok: false, reason: `too few frames (${series.length})` };
    if (series.length > o.maxFrames) return { ok: false, reason: 'too many frames' };
    for (let i = 0; i < series.length; i++) {
      const f = series[i];
      if (!f || !isNum(f.t) || !isNum(f.ear) || !isNum(f.yaw) || !isNum(f.cx) || !isNum(f.cy) || !isNum(f.w) || f.w <= 0) {
        return { ok: false, reason: `malformed frame ${i}` };
      }
      if (f.ear < 0 || f.ear > 1.5 || Math.abs(f.yaw) > 2) return { ok: false, reason: `implausible measurement in frame ${i}` };
      if (f.sm != null && (!isNum(f.sm) || f.sm < 0 || f.sm > 1)) return { ok: false, reason: `bad smile value in frame ${i}` };
      if (f.n != null && f.n > 1) return { ok: false, reason: 'more than one person in view' };
      if (i > 0) {
        const p = series[i - 1];
        const dt = f.t - p.t;
        if (dt <= 0) return { ok: false, reason: 'timestamps not increasing' };
        if (dt > o.maxGapMs) return { ok: false, reason: 'gap in the video stream' };
        const avgW = (f.w + p.w) / 2;
        const jump = Math.hypot(f.cx - p.cx, f.cy - p.cy) / avgW;
        const ratio = Math.max(f.w, p.w) / Math.min(f.w, p.w);
        if (jump > o.maxCenterJump || ratio > o.maxSizeRatio) return { ok: false, reason: 'face track jumped (possible swap)' };
      }
    }
    const duration = series[series.length - 1].t - series[0].t;
    if (duration < o.minDurationMs) return { ok: false, reason: 'challenge completed implausibly fast' };
    if (duration > o.maxDurationMs) return { ok: false, reason: 'challenge took too long' };
    return { ok: true, durationMs: duration };
  }

  /** Verify that `steps` (in order) really happened in `series`. */
  function evaluateChallenge(series, steps, optsIn) {
    const o = Object.assign({}, DEFAULTS, optsIn || {});
    if (!Array.isArray(steps) || !steps.length) return { ok: false, reason: 'no steps' };
    const basic = checkSeries(series, o);
    if (!basic.ok) return basic;
    const baseline = earBaseline(series);
    let from = 0;
    const results = [];
    for (const type of steps) {
      let idx;
      if (type === 'blink') idx = findBlink(series, from, baseline, o);
      else if (type === 'turn_left') idx = findTurn(series, from, +1, o);
      else if (type === 'turn_right') idx = findTurn(series, from, -1, o);
      else if (type === 'smile') idx = findSmile(series, from, o);
      else return { ok: false, reason: `unknown step "${type}"` };
      results.push({ type, ok: idx >= 0, index: idx });
      if (idx < 0) return { ok: false, reason: `step "${type}" not performed`, results, durationMs: basic.durationMs };
      from = idx;
    }
    return { ok: true, results, durationMs: basic.durationMs, baseline };
  }

  /** Is a single step done, looking only at frames from `from` on? Used live by the kiosk. */
  function stepProgress(type, series, from, optsIn) {
    const o = Object.assign({}, DEFAULTS, optsIn || {});
    if (type === 'blink') return findBlink(series, from, earBaseline(series), o);
    if (type === 'turn_left') return findTurn(series, from, +1, o);
    if (type === 'turn_right') return findTurn(series, from, -1, o);
    if (type === 'smile') return findSmile(series, from, o);
    return -1;
  }

  /** Compact numbers worth keeping in the event log. */
  function summarize(series) {
    const ears = series.map((f) => f.ear);
    const yaws = series.map((f) => f.yaw);
    const sm = series.map((f) => f.sm).filter(isNum);
    return {
      frames: series.length,
      durationMs: series.length ? Math.round(series[series.length - 1].t - series[0].t) : 0,
      earBaseline: Number(earBaseline(series).toFixed(3)),
      earMin: Number(Math.min(...ears).toFixed(3)),
      yawMin: Number(Math.min(...yaws).toFixed(2)),
      yawMax: Number(Math.max(...yaws).toFixed(2)),
      smileMax: sm.length ? Number(Math.max(...sm).toFixed(2)) : null,
    };
  }

  /**
   * Quality gate for a face sample. meta: { score, w, videoW, sharp, bright, yaw }.
   * Returns a list of human-readable problems (empty list = good enough to match on).
   */
  function probeIssues(meta, t, o) {
    const opts = Object.assign({}, DEFAULTS, o || {});
    const issues = [];
    if (!(meta.score >= t.minDetectionScore)) issues.push('face not clear');
    if (!(meta.w / meta.videoW >= t.minFaceWidthRatio)) issues.push('come closer');
    if (!(meta.sharp >= t.minSharpness)) issues.push('image blurry');
    if (!(meta.bright >= t.minBrightness)) issues.push('too dark');
    if (!(meta.bright <= t.maxBrightness)) issues.push('too bright');
    if (!(Math.abs(meta.yaw) <= opts.frontalYaw)) issues.push('look straight at the camera');
    return issues;
  }

  return {
    STEP_TYPES, DEFAULTS,
    earFromLandmarks, yawFromLandmarks, pitchFromLandmarks, rollFromLandmarks, boxOfLandmarks,
    eyeAspectRatio, percentile, median,
    earBaseline, findBlink, findTurn, findSmile, stepProgress,
    checkSeries, evaluateChallenge, summarize, probeIssues,
  };
});
